package com.partejar.app.feature.calendar.presentation

import androidx.compose.foundation.layout.Arrangement
import androidx.compose.foundation.layout.Box
import androidx.compose.foundation.layout.Column
import androidx.compose.foundation.layout.PaddingValues
import androidx.compose.foundation.layout.fillMaxSize
import androidx.compose.foundation.layout.fillMaxWidth
import androidx.compose.foundation.layout.height
import androidx.compose.foundation.layout.padding
import androidx.compose.foundation.lazy.LazyColumn
import androidx.compose.foundation.lazy.items
import androidx.compose.material3.Icon
import androidx.compose.material3.IconButton
import androidx.compose.material3.MaterialTheme
import androidx.compose.material3.Scaffold
import androidx.compose.material3.Text
import androidx.compose.runtime.Composable
import androidx.compose.runtime.collectAsState
import androidx.compose.runtime.getValue
import androidx.compose.ui.Alignment
import androidx.compose.ui.Modifier
import androidx.compose.ui.semantics.heading
import androidx.compose.ui.semantics.semantics
import androidx.compose.ui.tooling.preview.Preview
import com.partejar.app.core.ui.components.PartejarAlert
import com.partejar.app.core.ui.components.PartejarAlertTone
import com.partejar.app.core.ui.components.PartejarButton
import com.partejar.app.core.ui.components.PartejarButtonVariant
import com.partejar.app.core.ui.components.PartejarCard
import com.partejar.app.core.ui.components.PartejarSkeletonText
import com.partejar.app.core.ui.components.PartejarTopBar
import com.partejar.app.core.ui.theme.PartejarIcons
import com.partejar.app.core.ui.theme.PartejarTheme
import com.partejar.app.core.ui.theme.ThemeMode
import com.partejar.app.feature.calendar.CalendarPermissionStatus
import com.partejar.app.feature.calendar.rememberCalendarIntegration
import org.koin.compose.koinInject
import org.koin.core.parameter.parametersOf

@Composable
fun CalendarIntegrationScreen(onBack: () -> Unit) {
    val integration = rememberCalendarIntegration()
    val viewModel: CalendarIntegrationViewModel = koinInject(
        parameters = { parametersOf(integration) }
    )
    val state by viewModel.uiState.collectAsState()
    val actionState by viewModel.actionState.collectAsState()
    androidx.lifecycle.compose.LifecycleEventEffect(androidx.lifecycle.Lifecycle.Event.ON_RESUME) { viewModel.refresh() }
    CalendarIntegrationLayout(
        state = state,
        actionState = actionState,
        onBack = onBack,
        onRetry = viewModel::refresh,
        onRequestPermission = viewModel::requestPermission,
        onOpenSystemSettings = viewModel::openSystemSettings,
        onDismissError = viewModel::clearActionError,
        onDisable = viewModel::disable
    )
}

@Composable
private fun CalendarIntegrationLayout(
    state: CalendarIntegrationUiState,
    actionState: CalendarPermissionActionState,
    onBack: () -> Unit,
    onRetry: () -> Unit,
    onRequestPermission: () -> Unit,
    onOpenSystemSettings: () -> Unit,
    onDismissError: () -> Unit,
    onDisable: () -> Unit = {}
) {
    val spacing = PartejarTheme.spacing
    Scaffold(
        containerColor = MaterialTheme.colorScheme.background,
        topBar = {
            PartejarTopBar(
                title = "Calendário",
                leadingContent = {
                    IconButton(onClick = onBack) {
                        Icon(PartejarIcons.Actions.back, contentDescription = "Voltar")
                    }
                }
            )
        }
    ) { padding ->
        when (state) {
            CalendarIntegrationUiState.Loading -> CalendarLoading(Modifier.padding(padding))
            is CalendarIntegrationUiState.Error -> CalendarError(
                message = state.message,
                onRetry = onRetry,
                modifier = Modifier.padding(padding).padding(spacing.space24)
            )
            is CalendarIntegrationUiState.Content -> CalendarContent(
                permissionStatus = state.permissionStatus,
                actionState = actionState,
                onRefresh = onRetry,
                onRequestPermission = onRequestPermission,
                onOpenSystemSettings = onOpenSystemSettings,
                onDismissError = onDismissError,
                onDisable = onDisable,
                modifier = Modifier.padding(padding)
            )
        }
    }
}

@Composable
private fun CalendarContent(
    permissionStatus: CalendarPermissionStatus,
    actionState: CalendarPermissionActionState,
    onRefresh: () -> Unit,
    onRequestPermission: () -> Unit,
    onOpenSystemSettings: () -> Unit,
    onDismissError: () -> Unit,
    onDisable: () -> Unit,
    modifier: Modifier = Modifier
) {
    val spacing = PartejarTheme.spacing
    LazyColumn(
        modifier = modifier.fillMaxSize(),
        contentPadding = PaddingValues(
            start = spacing.space16,
            top = spacing.space16,
            end = spacing.space16,
            bottom = spacing.space32
        ),
        verticalArrangement = Arrangement.spacedBy(spacing.space16)
    ) {
        item {
            PartejarCard(modifier = Modifier.fillMaxWidth()) {
                Column(verticalArrangement = Arrangement.spacedBy(spacing.space12)) {
                    Icon(
                        PartejarIcons.Clinical.calendar,
                        contentDescription = null,
                        tint = MaterialTheme.colorScheme.primary
                    )
                    Text("Integração com calendário", style = MaterialTheme.typography.titleMedium)
                    Text(
                        "Ao ativar a integração, suas reuniões e eventos são adicionados à agenda do celular. Alterações feitas no Partejar atualizam os mesmos compromissos na agenda.",
                        color = MaterialTheme.colorScheme.onSurfaceVariant
                    )
                }
            }
        }
        item {
            Text(
                "Status do acesso",
                modifier = Modifier.semantics { heading() },
                style = MaterialTheme.typography.titleSmall,
                color = MaterialTheme.colorScheme.onSurfaceVariant
            )
        }
        item {
            PartejarAlert(
                title = permissionStatus.title(),
                tone = permissionStatus.alertTone()
            ) {
                Text(permissionStatus.description())
            }
        }
        item {
            CalendarPermissionAction(
                status = permissionStatus,
                requesting = actionState is CalendarPermissionActionState.Requesting,
                onRefresh = onRefresh,
                onRequestPermission = onRequestPermission,
                onOpenSystemSettings = onOpenSystemSettings
            )
            if (permissionStatus == CalendarPermissionStatus.GRANTED) {
                PartejarButton("Desativar integração", onDisable, variant = PartejarButtonVariant.TERTIARY)
            }
        }
        if (actionState is CalendarPermissionActionState.Error) {
            item {
                PartejarAlert(title = "Não foi possível concluir", tone = PartejarAlertTone.ERROR) {
                    Column(verticalArrangement = Arrangement.spacedBy(spacing.space8)) {
                        Text(actionState.message)
                        PartejarButton(
                            text = "Dispensar",
                            onClick = onDismissError,
                            variant = PartejarButtonVariant.TERTIARY
                        )
                    }
                }
            }
        }
    }
}

@Composable
private fun CalendarPermissionAction(
    status: CalendarPermissionStatus,
    requesting: Boolean,
    onRefresh: () -> Unit,
    onRequestPermission: () -> Unit,
    onOpenSystemSettings: () -> Unit
) {
    when (status) {
        CalendarPermissionStatus.NOT_DETERMINED,
        CalendarPermissionStatus.DENIED -> PartejarButton(
            text = "Permitir acesso",
            onClick = onRequestPermission,
            isLoading = requesting
        )

        CalendarPermissionStatus.BLOCKED,
        CalendarPermissionStatus.RESTRICTED -> Column(
            verticalArrangement = Arrangement.spacedBy(PartejarTheme.spacing.space8)
        ) {
            PartejarButton(
                text = "Abrir ajustes do sistema",
                onClick = onOpenSystemSettings,
                variant = PartejarButtonVariant.SECONDARY
            )
            PartejarButton(
                text = "Verificar novamente",
                onClick = onRefresh,
                variant = PartejarButtonVariant.TERTIARY
            )
        }

        CalendarPermissionStatus.GRANTED -> PartejarButton(
            text = "Acesso concedido",
            onClick = {},
            enabled = false,
            variant = PartejarButtonVariant.SECONDARY
        )

        CalendarPermissionStatus.UNAVAILABLE -> Unit
    }
}

@Composable
private fun CalendarLoading(modifier: Modifier = Modifier) = LazyColumn(
    modifier = modifier.fillMaxSize(),
    contentPadding = PaddingValues(PartejarTheme.spacing.space16),
    verticalArrangement = Arrangement.spacedBy(PartejarTheme.spacing.space16)
) {
    items(2) {
        PartejarCard(modifier = Modifier.fillMaxWidth()) {
            Column(verticalArrangement = Arrangement.spacedBy(PartejarTheme.spacing.space8)) {
                PartejarSkeletonText(Modifier.fillMaxWidth(0.58f).height(PartejarTheme.spacing.space16))
                PartejarSkeletonText(Modifier.fillMaxWidth().height(PartejarTheme.spacing.space12))
                PartejarSkeletonText(Modifier.fillMaxWidth(0.76f).height(PartejarTheme.spacing.space12))
            }
        }
    }
}

@Composable
private fun CalendarError(message: String, onRetry: () -> Unit, modifier: Modifier = Modifier) = Column(
    modifier = modifier,
    verticalArrangement = Arrangement.spacedBy(PartejarTheme.spacing.space16)
) {
    PartejarAlert(title = "Calendário indisponível", tone = PartejarAlertTone.ERROR) { Text(message) }
    PartejarButton(text = "Tentar novamente", onClick = onRetry)
}

internal fun CalendarPermissionStatus.title(): String = when (this) {
    CalendarPermissionStatus.NOT_DETERMINED -> "Acesso ainda não solicitado"
    CalendarPermissionStatus.GRANTED -> "Integração pronta"
    CalendarPermissionStatus.DENIED -> "Acesso não concedido"
    CalendarPermissionStatus.BLOCKED -> "Acesso bloqueado"
    CalendarPermissionStatus.RESTRICTED -> "Acesso restrito pelo dispositivo"
    CalendarPermissionStatus.UNAVAILABLE -> "Calendário indisponível nesta plataforma"
}

internal fun CalendarPermissionStatus.description(): String = when (this) {
    CalendarPermissionStatus.NOT_DETERMINED -> "Você decide quando permitir. O Partejar não acessa o calendário sem sua ação."
    CalendarPermissionStatus.GRANTED -> "Reuniões e eventos são sincronizados automaticamente com a agenda do celular."
    CalendarPermissionStatus.DENIED -> "O acesso foi recusado. Você pode tentar novamente quando quiser."
    CalendarPermissionStatus.BLOCKED -> "A permissão foi bloqueada. Altere-a nos ajustes do sistema para continuar."
    CalendarPermissionStatus.RESTRICTED -> "Este dispositivo não permite alterar essa permissão."
    CalendarPermissionStatus.UNAVAILABLE -> "A integração com calendário não está disponível nesta plataforma."
}

internal fun CalendarPermissionStatus.alertTone(): PartejarAlertTone = when (this) {
    CalendarPermissionStatus.GRANTED -> PartejarAlertTone.SUCCESS
    CalendarPermissionStatus.NOT_DETERMINED,
    CalendarPermissionStatus.UNAVAILABLE -> PartejarAlertTone.INFO
    CalendarPermissionStatus.DENIED,
    CalendarPermissionStatus.BLOCKED,
    CalendarPermissionStatus.RESTRICTED -> PartejarAlertTone.ATTENTION
}

@Preview(name = "Calendário · acesso pendente", widthDp = 390, heightDp = 844)
@Composable
private fun CalendarPendingPreview() = PartejarTheme(themeMode = ThemeMode.LIGHT) {
    CalendarIntegrationLayout(
        CalendarIntegrationUiState.Content(CalendarPermissionStatus.NOT_DETERMINED),
        CalendarPermissionActionState.Idle,
        {}, {}, {}, {}, {}
    )
}

@Preview(name = "Calendário · concedido", widthDp = 390, heightDp = 844)
@Composable
private fun CalendarGrantedPreview() = PartejarTheme(themeMode = ThemeMode.DARK) {
    CalendarIntegrationLayout(
        CalendarIntegrationUiState.Content(CalendarPermissionStatus.GRANTED),
        CalendarPermissionActionState.Idle,
        {}, {}, {}, {}, {}
    )
}

@Preview(name = "Calendário · bloqueado", widthDp = 390, heightDp = 844)
@Composable
private fun CalendarBlockedPreview() = PartejarTheme {
    CalendarIntegrationLayout(
        CalendarIntegrationUiState.Content(CalendarPermissionStatus.BLOCKED),
        CalendarPermissionActionState.Idle,
        {}, {}, {}, {}, {}
    )
}
